import { permissionLabel, type Permission } from '@bookora/shared';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  WHY A BUTTON IS GREY — the shared Persian reasons
 * ════════════════════════════════════════════════════════════════════════════
 *
 * The one place reason copy that more than one screen uses is composed. A
 * reason a single screen needs lives next to that screen as a local const.
 *
 * Every reason has two clauses, «[why]. [what to do].», and ends with «.».
 * Work in flight (uploading, saving) is never a reason: that is `loading`.
 * The grant path is always «مالک کلینیک» (the only preset that holds
 * `staff.manage_roles`) and the screen «کارکنان و نقش‌ها», because a
 * permission is granted per role.
 */

/**
 * «you need X; ask the clinic owner». Words the app-wide 403 page
 * (`app/require-permission.tsx`) already uses, with the permission named
 * exactly as the role editor shows it — from `permissionLabel()`, never
 * retyped at a call site.
 *
 * Shown only to holders of a CUSTOM role (see `useExplainedPermission`): a
 * system role that lacks a key lacks it by design.
 */
export function permissionReason(permission: Permission): string {
  return permissionCopy(permission, { customRole: true }).reason;
}

/**
 * What to say when the viewer lacks a permission (critic C18) — ONE policy:
 *
 *  · the permission is always NAMED, from `permissionLabel()`;
 *  · the grant path («از مالک کلینیک بخواید … «کارکنان و نقش‌ها» …») only for a
 *    CUSTOM role, whose owner can actually add the key;
 *  · a system role (پزشک، پرستار، منشی، حسابدار) cannot be edited, so it is told
 *    the truth instead: not possible with this role; tell the owner if needed.
 *
 * `title` fits an EmptyState/Alert heading; `reason` is a full «[why]. [what to
 * do].» sentence for `disabledReason`, a toast or a description; `next` is its
 * second clause alone, for a description under a `title`. Whether to SHOW a
 * blocked control to a system role at all is still `useExplainedPermission`'s
 * call (default: hide it) — this is only the words once something is shown or
 * the server has already refused.
 *
 * In a component, `usePermissionCopy(p)` (`@/permissions`) reads the role.
 */
export interface PermissionCopy {
  /** «دسترسی «…» رو ندارید» */
  title: string;
  /** «برای این کار دسترسی «…» لازم است. <next>» */
  reason: string;
  /** The what-to-do clause alone, ending with «.». */
  next: string;
}

/** The grant path, for a custom role. */
export const PERMISSION_GRANT_NEXT =
  'از مالک کلینیک بخواید اون رو در بخش «کارکنان و نقش‌ها» به نقش شما بده.';
/** A system role cannot be edited: say so, and whom to tell. */
export const PERMISSION_SYSTEM_ROLE_NEXT =
  'با نقش فعلی نمی‌تونید این کار رو انجام بدید. اگر بهش نیاز دارید، به مالک کلینیک اطلاع بدید.';

export function permissionCopy(
  permission: Permission,
  opts: { customRole: boolean },
): PermissionCopy {
  const label = permissionLabel(permission);
  const next = opts.customRole ? PERMISSION_GRANT_NEXT : PERMISSION_SYSTEM_ROLE_NEXT;
  return {
    title: `دسترسی «${label}» رو ندارید`,
    reason: `برای این کار دسترسی «${label}» لازم است. ${next}`,
    next,
  };
}

/**
 * The permission picker's lock: the viewer cannot grant a key they do not hold
 * themselves (the grant ceiling). Also the invite dialog and a starter-kit
 * refusal.
 */
export const GRANT_CEILING_REASON =
  'خودتون این سطح دسترسی رو ندارید. برای دادن اون به دیگران، از مالک کلینیک کمک بگیرید.';

/**
 * «تأیید» on a permanent-erasure request the viewer filed themselves, in a
 * clinic with more than one active staff member (separation of duties; the
 * server's `assertSeparationOfDuties`). A solo clinic approves its own.
 */
export const ERASURE_SELF_APPROVE_REASON =
  'این درخواست رو خودتون ثبت کردید. همکار دیگری باید اون رو تأیید کنه.';

/** The seat limit of the plan is reached. The holder of billing can act on it; others ask. */
export function seatLimitReason(canManageBilling: boolean): string {
  return canManageBilling
    ? 'ظرفیت کارکنان پلن پر است. برای دعوت همکار جدید، از «تنظیمات › اشتراک» پلن رو ارتقا بدید.'
    : 'ظرفیت کارکنان پلن پر است. برای ارتقای پلن به مالک کلینیک خبر بدید.';
}

/**
 * A write the clinic's plan does not include (owner D6; 402
 * FEATURE_NOT_IN_PLAN). `plan` is the plan that has it, by the server's name
 * (`requiredPlanName`), never typed here. The `settings.billing` holder is told
 * where to upgrade; everyone else whom to tell — «مدیر کلینیک», owner round 4.
 * The same sentence is a create button's `disabledReason` inside a read-only
 * section and the body of the refusal notification.
 */
export function planLockReason(plan: string, canBilling: boolean): string {
  return canBilling
    ? `این قابلیت در پلن ${plan} ارائه می‌شه. برای ثبت اطلاعات جدید، از «تنظیمات › اشتراک» پلن رو ارتقا بدید.`
    : `این قابلیت در پلن ${plan} ارائه می‌شه. برای ارتقای پلن به مدیر کلینیک خبر بدید.`;
}

/** State blockers more than one screen hits. */
export const REASON = {
  NOTHING_SELECTED: 'هنوز چیزی انتخاب نشده. اول حداقل یک مورد رو انتخاب کنید.',
  /** The promo-code field's «اعمال کد» before a plausible code is typed. */
  OFFER_CODE_SHORT: 'کد تخفیف کوتاهه. کد کامل رو وارد کنید.',
  NOTHING_TO_EXPORT:
    'در این بازه اطلاعاتی برای خروجی گرفتن وجود نداره. بازهٔ دیگری رو انتخاب کنید.',
  /**
   * The clinic's subscription lapsed into read-only (billing): every write is
   * refused with 402 until renewal. For a write CTA that stays visible.
   */
  CLINIC_READ_ONLY:
    'اطلاعات کلینیک فقط قابل مشاهده است. بعد از تمدید اشتراک، ثبت و ویرایش دوباره فعال می‌شه.',
} as const;
